<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useRouter } from "vue-router";
import { useDashboardAppStore } from "@/stores/dashboardApp";
import type { DashboardApp } from "@/types/common";
import constants from "@/constant";

const router = useRouter();
const dashboardApp = useDashboardAppStore();
const { selectedApp } = storeToRefs(dashboardApp);

const { DASHBOARD_APPS, DASHBOARD_APP_LABELS, DASHBOARD_APP_ROUTES } =
  constants;
const appOptions = (
  Object.keys(DASHBOARD_APPS) as (keyof typeof DASHBOARD_APPS)[]
).map((appKey) => ({
  value: DASHBOARD_APPS[appKey] as DashboardApp,
  label: DASHBOARD_APP_LABELS[appKey],
  routeName: DASHBOARD_APP_ROUTES[appKey],
}));

const onAppChange = (changeEvent: Event) => {
  const selectedValue = (changeEvent.target as HTMLSelectElement).value;
  const selectedOption = appOptions.find(
    (appOption) => appOption.value === selectedValue,
  );
  if (!selectedOption) return;
  dashboardApp.setSelectedApp(selectedOption.value);
  router.push({ name: selectedOption.routeName });
};
</script>

<template>
  <div class="app-switcher">
    <pn-select
      select-id="dashboard-app-select"
      name="dashboardApp"
      :label="DASHBOARD_APP_LABELS.SELECT_LABEL"
      @change="onAppChange"
    >
      <option
        v-for="appOption in appOptions"
        :key="appOption.value"
        :value="appOption.value"
        :selected="appOption.value === selectedApp"
      >
        {{ appOption.label }}
      </option>
    </pn-select>
  </div>
</template>

<style lang="scss" scoped>
.app-switcher {
  line-height: normal;
  pn-select {
    width: 180px;
  }
  // Label kept for screen readers only; the topbar has no room for it.
  :deep(.pn-select-label) {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
  :deep(.pn-select-element) {
    padding: 0.4em 2.25em 0.4em 0.75em;
    font-size: 0.875em;
    border-color: transparent;
  }
  :deep(.pn-select-icon[data-default]) {
    right: 0.5em;
  }
}
</style>
